import { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Card, Typography, Space, Select, Statistic, Row, Col, Tag, Input, Empty, Button, Descriptions } from 'antd';
import { PauseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Resizable, type ResizeCallbackData } from 'react-resizable';
import 'react-resizable/css/styles.css';
import apiClient from '../api/index';
import { formatTimestamp } from '../utils/date';

const { Title, Text } = Typography;

// Resizable header cell for Ant Design Table
const ResizableTitle = (
  props: React.HTMLAttributes<HTMLTableCellElement> & {
    onResize?: (e: React.SyntheticEvent, data: ResizeCallbackData) => void;
    width?: number;
  }
) => {
  const { onResize, width, ...restProps } = props;

  if (!width || !onResize) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          style={{ position: 'absolute', right: -5, bottom: 0, top: 0, width: 10, cursor: 'col-resize', zIndex: 1 }}
          onClick={(e) => e.stopPropagation()}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  );
};

interface LogEntry {
  id: number;
  timestamp: string;
  client_name: string | null;
  auth_type: 'api_key' | 'jwt' | 'none';
  username: string;
  ip_address: string;
  method: string;
  endpoint: string;
  request_params: Record<string, unknown> | string | null;
  response_status: number;
  result_count: number;
  product_ids: string[] | string | null;
  response_time_ms: number | null;
}

interface LogsResponse {
  total: number;
  limit: number;
  offset: number;
  logs: LogEntry[];
}

interface ClientSummary {
  client_name: string | null;
  auth_type: string;
  request_count: number;
  avg_response_ms: number | null;
  total_results: number;
  first_seen: string;
  last_seen: string;
}

interface LogsSummary {
  days: number;
  by_client: ClientSummary[];
  by_endpoint: Array<{
    endpoint: string;
    method: string;
    request_count: number;
    avg_response_ms: number | null;
    avg_results: number | null;
  }>;
}

function formatParams(params: Record<string, unknown> | string | null): string {
  if (!params) return '-';
  try {
    const obj = typeof params === 'string' ? JSON.parse(params) : params;
    if (!obj || typeof obj !== 'object') return String(params);
    const entries = Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== '');
    if (entries.length === 0) return '-';
    return entries.map(([k, v]) => `${k}=${v}`).join(', ');
  } catch {
    return String(params);
  }
}

export function LogsPage() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [summary, setSummary] = useState<LogsSummary | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(7);
  const [page, setPage] = useState(1);
  const [clientFilter, setClientFilter] = useState('');
  const [refreshInterval, setRefreshInterval] = useState(10);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const pageSize = 50;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        days: String(days),
        limit: String(pageSize),
        offset: String((page - 1) * pageSize),
      });
      if (clientFilter) params.set('client', clientFilter);

      const [logsRes, summaryRes] = await Promise.all([
        apiClient.get<LogsResponse>(`/api/v1/logs?${params}`),
        apiClient.get<LogsSummary>(`/api/v1/logs/summary?days=${days}`),
      ]);
      setLogs(logsRes.data.logs || []);
      setTotal(logsRes.data.total || 0);
      setSummary(summaryRes.data);
    } catch (err: unknown) {
      console.error('Failed to fetch logs:', err);
      const axiosErr = err as { response?: { data?: { message?: string }; status?: number } };
      if (axiosErr?.response?.status === 403) {
        setError('Admin access required to view API logs.');
      } else {
        setError(axiosErr?.response?.data?.message || 'Failed to load API logs. The server may still be deploying.');
      }
    } finally {
      setLoading(false);
    }
  }, [days, page, clientFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Auto-refresh timer
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (!paused && refreshInterval > 0) {
      intervalRef.current = setInterval(() => {
        fetchLogs();
      }, refreshInterval * 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [paused, refreshInterval, fetchLogs]);

  const [colWidths, setColWidths] = useState({
    timestamp: 170,
    client_name: 130,
    endpoint: 250,
    request_params: 200,
    response_status: 80,
    result_count: 80,
    response_time_ms: 90,
    ip_address: 120,
  });

  const handleResize = (key: string) =>
    (_: React.SyntheticEvent, { size }: ResizeCallbackData) => {
      setColWidths((prev) => ({ ...prev, [key]: size.width }));
    };

  const columns: ColumnsType<LogEntry> = [
    {
      title: 'Time',
      dataIndex: 'timestamp',
      width: colWidths.timestamp,
      onHeaderCell: () => ({ width: colWidths.timestamp, onResize: handleResize('timestamp') }) as React.HTMLAttributes<HTMLTableCellElement>,
      render: (ts: string) => formatTimestamp(ts),
    },
    {
      title: 'Client',
      dataIndex: 'client_name',
      width: colWidths.client_name,
      onHeaderCell: () => ({ width: colWidths.client_name, onResize: handleResize('client_name') }) as React.HTMLAttributes<HTMLTableCellElement>,
      render: (name: string | null, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name || '-'}</Text>
          <Tag color={record.auth_type === 'api_key' ? 'blue' : record.auth_type === 'jwt' ? 'green' : 'default'}>
            {record.auth_type}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Endpoint',
      dataIndex: 'endpoint',
      width: colWidths.endpoint,
      onHeaderCell: () => ({ width: colWidths.endpoint, onResize: handleResize('endpoint') }) as React.HTMLAttributes<HTMLTableCellElement>,
      render: (endpoint: string, record) => (
        <Text code>{record.method} {endpoint}</Text>
      ),
    },
    {
      title: 'Params',
      dataIndex: 'request_params',
      width: colWidths.request_params,
      ellipsis: true,
      onHeaderCell: () => ({ width: colWidths.request_params, onResize: handleResize('request_params') }) as React.HTMLAttributes<HTMLTableCellElement>,
      render: (params: Record<string, unknown> | string | null) => formatParams(params),
    },
    {
      title: 'Status',
      dataIndex: 'response_status',
      width: colWidths.response_status,
      onHeaderCell: () => ({ width: colWidths.response_status, onResize: handleResize('response_status') }) as React.HTMLAttributes<HTMLTableCellElement>,
      render: (status: number) => (
        <Tag color={status < 400 ? 'green' : status < 500 ? 'orange' : 'red'}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Results',
      dataIndex: 'result_count',
      width: colWidths.result_count,
      align: 'right' as const,
      onHeaderCell: () => ({ width: colWidths.result_count, onResize: handleResize('result_count') }) as React.HTMLAttributes<HTMLTableCellElement>,
    },
    {
      title: 'Time (ms)',
      dataIndex: 'response_time_ms',
      width: colWidths.response_time_ms,
      align: 'right' as const,
      onHeaderCell: () => ({ width: colWidths.response_time_ms, onResize: handleResize('response_time_ms') }) as React.HTMLAttributes<HTMLTableCellElement>,
      render: (ms: number | null) => ms != null ? `${ms}` : '-',
    },
    {
      title: 'IP',
      dataIndex: 'ip_address',
      width: colWidths.ip_address,
      onHeaderCell: () => ({ width: colWidths.ip_address, onResize: handleResize('ip_address') }) as React.HTMLAttributes<HTMLTableCellElement>,
    },
  ];

  const expandedRow = (record: LogEntry) => {
    let params: Record<string, unknown> | null = null;
    try {
      params = typeof record.request_params === 'string'
        ? JSON.parse(record.request_params)
        : record.request_params;
    } catch { /* ignore */ }

    let productIds: string[] = [];
    try {
      if (Array.isArray(record.product_ids)) {
        productIds = record.product_ids;
      } else if (typeof record.product_ids === 'string') {
        productIds = JSON.parse(record.product_ids);
      }
    } catch { /* ignore */ }

    return (
      <Descriptions column={2} size="small" bordered>
        <Descriptions.Item label="Log ID">{record.id}</Descriptions.Item>
        <Descriptions.Item label="Timestamp">
          {formatTimestamp(record.timestamp)} ({record.timestamp})
        </Descriptions.Item>
        <Descriptions.Item label="Client">{record.client_name || 'Anonymous'}</Descriptions.Item>
        <Descriptions.Item label="Auth Type">
          <Tag color={record.auth_type === 'api_key' ? 'blue' : record.auth_type === 'jwt' ? 'green' : 'default'}>
            {record.auth_type}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Username">{record.username || '-'}</Descriptions.Item>
        <Descriptions.Item label="IP Address">{record.ip_address}</Descriptions.Item>
        <Descriptions.Item label="Endpoint">
          <Text code>{record.method} {record.endpoint}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Response Status">
          <Tag color={record.response_status < 400 ? 'green' : record.response_status < 500 ? 'orange' : 'red'}>
            {record.response_status}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Result Count">{record.result_count}</Descriptions.Item>
        <Descriptions.Item label="Response Time">
          {record.response_time_ms != null ? `${record.response_time_ms}ms` : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Request Parameters" span={2}>
          {params ? (
            <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(params, null, 2)}
            </pre>
          ) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Product IDs Returned" span={2}>
          {productIds.length > 0 ? (
            <Space wrap>
              {productIds.map((pid, i) => (
                <Tag key={i} color="blue" style={{ cursor: 'pointer' }}
                     onClick={() => window.open(`/products/${pid}`, '_blank')}>
                  {pid}
                </Tag>
              ))}
            </Space>
          ) : '-'}
        </Descriptions.Item>
      </Descriptions>
    );
  };

  if (error) {
    return (
      <div>
        <Title level={4}>API Request Logs</Title>
        <Empty description={error} />
      </div>
    );
  }

  return (
    <div>
      <Title level={4}>API Request Logs</Title>

      {/* Summary Cards */}
      {summary && summary.by_client && summary.by_client.length > 0 && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          {summary.by_client.map((client, i) => (
            <Col key={i} xs={12} sm={8} md={6}>
              <Card size="small">
                <Statistic
                  title={client.client_name || 'Anonymous'}
                  value={client.request_count}
                  suffix="requests"
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Avg {Math.round(client.avg_response_ms || 0)}ms
                </Text>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Filters */}
      <Space style={{ marginBottom: 16 }}>
        <Select
          value={days}
          onChange={(v) => { setDays(v); setPage(1); }}
          options={[
            { value: 1, label: 'Last 24 hours' },
            { value: 7, label: 'Last 7 days' },
            { value: 30, label: 'Last 30 days' },
            { value: 90, label: 'Last 90 days' },
          ]}
          style={{ width: 150 }}
        />
        <Input.Search
          placeholder="Filter by client..."
          allowClear
          onSearch={(v) => { setClientFilter(v); setPage(1); }}
          style={{ width: 200 }}
        />
        <Text type="secondary">{total} total requests</Text>
        <Select
          value={refreshInterval}
          onChange={setRefreshInterval}
          options={[
            { value: 5, label: 'Every 5s' },
            { value: 10, label: 'Every 10s' },
            { value: 30, label: 'Every 30s' },
            { value: 60, label: 'Every 60s' },
          ]}
          style={{ width: 120 }}
        />
        <Button
          type={paused ? 'primary' : 'default'}
          icon={paused ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
          onClick={() => setPaused((p) => !p)}
        >
          {paused ? 'Resume' : 'Pause'}
        </Button>
      </Space>

      {/* Log Table */}
      <Table
        columns={columns}
        dataSource={logs}
        rowKey="id"
        loading={loading}
        size="small"
        components={{ header: { cell: ResizableTitle } }}
        expandable={{
          expandedRowRender: expandedRow,
          expandRowByClick: true,
        }}
        pagination={{
          current: page,
          total,
          pageSize,
          onChange: setPage,
          showSizeChanger: false,
        }}
      />
    </div>
  );
}
